import React, { useEffect, useState, useRef } from 'react';
import {
  View, Keyboard, TouchableWithoutFeedback, Platform,
  Dimensions,
  TouchableOpacity,
  Pressable
} from 'react-native';

import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import {
  VFInput,
  Toggle,
  Icon,
  VFText,
  VFButton,
  Shimmer,
} from '@vfgroup-oneplatform/foundation/Components';
import { useTheme, LightThemeColors } from '@vfgroup-oneplatform/foundation/Components/Themes';

import FrameworkTestKeys from '../../../FrameworkTestKeys';
import { getThemeImages } from '../../../Themes';
import useAddCardsValidation from '../../Hooks/useAddCardsValidation';
import {
  cardNumberFormatter,
  cvvFormater,
  nameOnCardFormatter,
  expirationDataFormatter,
} from '../../Utils/textFormatters';

import { loadingConfig, shimmerPlaceholderConfig } from './AddPaymentCard.LoadingConfig';
import styles from './AddPaymentCard.Styles';

const inputFonstSize = {
  labelFontSize: 14,
  fontSize: 16,
};
const _keyboardDidHide = (keyboardHeight, setkeyboardHeight, onKeyboardDidHide) => {
  setkeyboardHeight({ ...keyboardHeight, keyboardOpend: 0 });
  onKeyboardDidHide();
};
const _keyboardDidShow = (
  _keyboardHeight,
  keyboardHeight,
  setkeyboardHeight,
  onKeyboardDidShow,
) => {
  setkeyboardHeight({
    ...keyboardHeight,
    keyboardOpend: _keyboardHeight,
  });
  onKeyboardDidShow(_keyboardHeight);
};

const Wrapper = ({ children, onPress = () => Keyboard.dismiss }) => {
  if (Platform.OS === 'web') {
    return (<>
      {children}
    </>)
  }
  return (
    <TouchableWithoutFeedback onPress={onPress}>
      {children}
    </TouchableWithoutFeedback>
  )
}

function AddPaymentCard({
  contentContainerStyle,
  inputContainerStyle,
  halfSizeContainerStyle,
  cardNumberLabel,
  nameOnCardLabel,
  expirationDateLabel,
  expirationDateFormatLabel,
  cvvNumberLabel,
  cardNameLabel,
  saveCardText,
  secureText,
  primaryButtonText,
  onPressSaveCard,
  hiddenFields,
  hideSaveCardForLater,
  isSecureContainerBottomBorder,
  isSecureIcon,
  secureTextStyle,
  cardData,
  validations,
  isCardDataLoading,
  onKeyboardDidHide,
  onKeyboardDidShow,
  setModalIsVisible,
  isEditMood,
  savingError,
  saveForLaterIntialValue,
  shouldShowTempCardName,
  withHandleKeyboardOpen,
  goBackView,
  isCenter
}) {
  const formFields = useRef({});
  const [keyboardHeight, setkeyboardHeight] = useState(0);
  const [state, setstate] = useState({ saveForLater: saveForLaterIntialValue });
  const [isFieldFocused, setIsFieldFocused] = useState({});
  const { validators, isAllInputsVaild } = useAddCardsValidation(state, hiddenFields, validations);
  const [hasSavingError, setHasSavingError] = useState(false);
  useEffect(() => {
    if (savingError != undefined && savingError.isAlreadyExistingError) {
      setHasSavingError(true);
    }
  }, [savingError]);

  useEffect(() => {
    // set form values once card data loading is finished
    for (const fieldName in cardData) {
      formFields.current[fieldName]?.setValue(cardData[fieldName]);
    }
    setstate({ ...state, ...cardData });
  }, [isCardDataLoading]);
  const theme = useTheme() || LightThemeColors;
  const images = getThemeImages(theme.name);
  useEffect(() => {
    const keboardDidShowListener = Keyboard.addListener('keyboardDidShow', e =>
      _keyboardDidShow(
        e.endCoordinates.height,
        keyboardHeight,
        setkeyboardHeight,
        onKeyboardDidShow,
      ),
    );
    const keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', () =>
      _keyboardDidHide(keyboardHeight, setkeyboardHeight, onKeyboardDidHide),
    );
    return () => {
      keboardDidShowListener.remove();
      keyboardDidHideListener.remove();
    };
  }, []);
  const handleFieldFocus = (name, value) => {
    setIsFieldFocused({ ...isFieldFocused, [name]: value });
  };
  const handleInput = (name, value) => {
    setstate({ ...state, [name]: value });
    setHasSavingError(false);
  };
  const handleInputs = values => {
    setstate({ ...state, ...values });
    setHasSavingError(false);
  };
  const handleOnPressAdd = () => {
    const isValidInputs = isAllInputsVaild();
    if (!isValidInputs) {
      return;
    }
    onPressSaveCard(state);
  };
  const shouldShowErrorText = name => {
    return validators[name].error && state[name] && !isFieldFocused[name];
  };
  const errorProps = isHasError => {
    return {
      ...theme.colors.textInput[isHasError ? 'error' : 'default'],
      showHelperText: isHasError,
      showRightIcon: isHasError,
    };
  };
  const momErroProps = memoize(errorProps);
  if (isCardDataLoading) {
    return (
      <Shimmer
        animationStyle={'ShineOverlay'}
        config={loadingConfig}
        shimmerPlaceholderConfig={shimmerPlaceholderConfig}
      />
    );
  }
  const onToggleChange = value => {
    //if shouldShowTempCardName is true or saveForLater will be true make value empty else make it with temporary placeholder
    const cardNameVal =
      shouldShowTempCardName || value ? '' : 'quick_action_add_new_payment_temp_card_name';

    handleInputs({
      saveForLater: value,
      cardName: cardNameVal,
    });
  };
  const SaveCardForLater = () => (
    <View style={styles.saveToggleConianer}>
      <Toggle
        initialValue={saveForLaterIntialValue}
        onChange={onToggleChange}
        testID={FrameworkTestKeys.SaveCardForLaterToggle}
      />
      <VFText i18nKey={saveCardText} style={styles.saveToggleText(theme)} />
    </View>
  );
  const isCardNameVisible =
    !hiddenFields.includes('cardName') && (state.saveForLater || shouldShowTempCardName);
  return (
    <Wrapper onPress={Keyboard.dismiss}>
      <View style={contentContainerStyle} testID={FrameworkTestKeys.AddPaymentCardView}>
        {Platform.OS === 'web' && Dimensions.get('window').width > 700 && !isCenter ?
          <Pressable
            onPress={goBackView}
          >
            <Icon
              name={images.ic_chevron_left_black_white}
              size={25}
              resizeMode="contain"
              type="image"
              style={{ marginTop: '-80px' }}
            />
          </Pressable>
          : null}
        <VFInput
          {...inputFonstSize}
          ref={inputRef => (formFields.current.cardNo = inputRef)}
          label={cardNumberLabel}
          value={state.cardNo}
          onChangeText={value => handleInput('cardNo', value)}
          formatText={cardNumberFormatter}
          onFocus={() => {
            handleFieldFocus('cardNo', true);
          }}
          onBlur={() => {
            handleFieldFocus('cardNo', false);
          }}
          keyboardType="decimal-pad"
          maxLength={19}
          rightIconName={images.ic_warning_mid_active}
          {...momErroProps(shouldShowErrorText('cardNo'))}
          helperText="payment_add_form_card_number_error_message"
          testID={FrameworkTestKeys.CardNo}
          helperTextTestID={FrameworkTestKeys.CardNoHelperText}
          rigthIconTestID={FrameworkTestKeys.CardNoRightIcon}
          helperTextColor={theme.colors.textInput.error.tintColor}
          rightIconColor={theme.colors.inputIconColor}
          textColor={theme.colors.textColor}
        // containerStyle={[ inputContainerStyle]}
        />
        {!hiddenFields.includes('nameOnCard') && (
          <VFInput
            {...inputFonstSize}
            ref={inputRef => (formFields.current.nameOnCard = inputRef)}
            label={nameOnCardLabel}
            formatText={nameOnCardFormatter}
            onFocus={() => {
              handleFieldFocus('nameOnCard', true);
            }}
            onBlur={() => {
              handleFieldFocus('nameOnCard', false);
            }}
            onChangeText={value => handleInput('nameOnCard', value)}
            rightIconName={images.ic_warning_mid_active}
            {...momErroProps(shouldShowErrorText('nameOnCard'))}
            helperText="payment_add_form_name_on_card_error_message"
            testID={FrameworkTestKeys.NameOnCard}
            helperTextTestID={FrameworkTestKeys.NameOnCardHelperText}
            rigthIconTestID={FrameworkTestKeys.NameOnCardRightIcon}
            helperTextColor={theme.colors.textInput.error.tintColor}
            rightIconColor={theme.colors.inputIconColor}
            textColor={theme.colors.textColor}
          // containerStyle={[styles.inputContainer, inputContainerStyle]}
          />
        )}
        <View style={styles.twoInputsContainer}>
          <VFInput
            {...inputFonstSize}
            ref={inputRef => (formFields.current.expirationData = inputRef)}
            label={expirationDateLabel}
            containerStyle={[styles.halfSizeInput, halfSizeContainerStyle, { marginRight: 8 }]}
            onChangeText={value => handleInput('expirationData', value)}
            formatText={expirationDataFormatter}
            keyboardType="decimal-pad"
            onFocus={() => {
              handleFieldFocus('expirationData', true);
            }}
            onBlur={() => {
              handleFieldFocus('expirationData', false);
            }}
            maxLength={5}
            rightIconName={images.ic_warning_mid_active}
            customHelperText={
              <>
                {shouldShowErrorText('expirationData') ? (
                  <VFText
                    i18nKey="payment_add_form_expiration_date_error_message"
                    style={[styles.customHelperText(theme), styles.helperError(theme)]}
                  />
                ) : (
                  <VFText
                    i18nKey={expirationDateFormatLabel}
                    style={styles.customHelperText(theme)}
                  />
                )}
              </>
            }
            {...momErroProps(shouldShowErrorText('expirationData'))}
            showHelperText={true}
            rightIconColor={theme.colors.inputIconColor}
            textColor={theme.colors.textColor}
            testID={FrameworkTestKeys.ExpiryDate}
            rigthIconTestID={FrameworkTestKeys.ExpiryDateRightIcon}
          />
          <VFInput
            {...inputFonstSize}
            ref={inputRef => (formFields.current.ccvNo = inputRef)}
            label={cvvNumberLabel}
            containerStyle={[styles.halfSizeInput, halfSizeContainerStyle]}
            onChangeText={value => handleInput('ccvNo', value)}
            secureTextEntry
            formatText={cvvFormater}
            onFocus={() => {
              handleFieldFocus('ccvNo', true);
            }}
            onBlur={() => {
              handleFieldFocus('ccvNo', false);
            }}
            keyboardType="decimal-pad"
            maxLength={4}
            {...momErroProps(shouldShowErrorText('ccvNo'))}
            helperText="payment_add_form_cvv_number_error_message"
            showRightIcon={true}
            rightIconName={images.icCvv}
            helperTextColor={theme.colors.textInput.error.tintColor}
            rightIconColor={theme.colors.inputIconColor}
            textColor={theme.colors.textColor}
            testID={FrameworkTestKeys.CvvNo}
            helperTextTestID={FrameworkTestKeys.CvvNoHelperText}
          />
        </View>
        {!hideSaveCardForLater && SaveCardForLater()}
        {isCardNameVisible && (
          <VFInput
            {...inputFonstSize}
            ref={inputRef => (formFields.current.cardName = inputRef)}
            onFocus={() => {
              handleFieldFocus('cardName', true);
            }}
            onBlur={() => {
              handleFieldFocus('cardName', false);
            }}
            label={cardNameLabel}
            onChangeText={value => handleInput('cardName', value)}
            rightIconName={images.ic_warning_mid_active}
            {...momErroProps(shouldShowErrorText('cardName'))}
            helperTextColor={theme.colors.textInput.error.tintColor}
            rightIconColor={theme.colors.inputIconColor}
            textColor={theme.colors.textColor}
            // containerStyle={[styles.cardNameInputContainer, inputContainerStyle]}
            textInputContainerStyle={styles.textInputContainerStyle}
            testID={FrameworkTestKeys.CardName}
            helperTextTestID={FrameworkTestKeys.CardNameHelperText}
            rigthIconTestID={FrameworkTestKeys.CardNameRightIcon}
          />
        )}

        <View
          style={[styles.secureContainer(theme, isSecureContainerBottomBorder, isCardNameVisible)]}
        >
          {isSecureIcon && (
            <Icon type="image" name={images.icPadlockClose} size={36} style={styles.lockIcon} />
          )}
          <VFText i18nKey={secureText} style={[styles.secureText(theme), secureTextStyle]} />
        </View>
        <VFButton
          title={primaryButtonText}
          style={styles.primaryButton(theme)}
          textStyle={styles.primaryButtonText(theme)}
          onPress={handleOnPressAdd}
          disabled={!isAllInputsVaild() || hasSavingError}
          disabledStyle={styles.primaryButtonDisabled(theme)}
          disabledTextStyle={styles.primaryButtonDisabledText(theme)}
          testID={FrameworkTestKeys.SaveCardBtn}
        />
        {isEditMood && (
          <VFButton
            title="payment_delete_form_delete_button"
            style={styles.deleteButton(theme)}
            type="secondary"
            onPress={setModalIsVisible}
          />
        )}
        {withHandleKeyboardOpen && <View style={{ height: keyboardHeight.keyboardOpend }} />}
      </View>
    </Wrapper>
  );
}
AddPaymentCard.defaultProps = {
  cardNumberLabel: 'payment_add_form_card_number',
  nameOnCardLabel: 'payment_add_form_name_on_card',
  expirationDateLabel: 'payment_add_form_expiration_date',
  expirationDateFormatLabel: 'MM/YY',
  cvvNumberLabel: 'payment_add_form_cvv_number',
  cardNameLabel: 'payment_add_form_card_name',
  saveCardText: 'payment_add_form_toggle_text',
  secureText: 'payment_add_form_security_notice',
  primaryButtonText: 'payment_add_form_save_button',
  hiddenFields: [],
  hideSaveCardForLater: false,
  isSecureIcon: true,
  cardData: {},
  onPressSaveCard: () => { },
  onKeyboardDidHide: () => { },
  onKeyboardDidShow: () => { },
  validations: {},
  isSecureContainerBottomBorder: false,
  saveForLaterIntialValue: true,
  shouldShowTempCardName: false,
  withHandleKeyboardOpen: true,
};
AddPaymentCard.propTypes = {
  contentContainerStyle: ViewPropTypes.style,
  inputContainerStyle: ViewPropTypes.style,
  halfSizeContainerStyle: ViewPropTypes.style,
  cardNumberLabel: PropTypes.string,
  nameOnCardLabel: PropTypes.string,
  expirationDateLabel: PropTypes.string,
  expirationDateFormatLabel: PropTypes.string,
  cvvNumberLabel: PropTypes.string,
  cardNameLabel: PropTypes.string,
  saveCardText: PropTypes.string,
  secureText: PropTypes.string,
  primaryButtonText: PropTypes.string,
  onPressSaveCard: PropTypes.func,
  hiddenFields: PropTypes.arrayOf(PropTypes.string),
  hideSaveCardForLater: PropTypes.bool,
  isSaveCardBeforeSecure: PropTypes.bool,
  isSecureContainerBottomBorder: PropTypes.bool,
  isSecureIcon: PropTypes.bool,
  secureTextStyle: TextPropTypes.style,
  cardData: PropTypes.shape({
    cardName: PropTypes.string,
    nameOnCard: PropTypes.string,
    expirationData: PropTypes.string,
    ccvNo: PropTypes.string,
    cardNo: PropTypes.string,
  }),
  validations: PropTypes.shape({
    cardName: PropTypes.func,
    nameOnCard: PropTypes.func,
    expirationData: PropTypes.func,
    ccvNo: PropTypes.func,
    cardNo: PropTypes.func,
  }),
  onKeyboardDidHide: PropTypes.func,
  onKeyboardDidShow: PropTypes.func,
  isCardDataLoading: PropTypes.bool,
  setModalIsVisible: PropTypes.func,
  isEditMood: PropTypes.bool,
  savingError: PropTypes.object,
  saveForLaterIntialValue: PropTypes.bool,
  shouldShowTempCardName: PropTypes.bool,
  withHandleKeyboardOpen: PropTypes.bool,
};
export { _keyboardDidHide, _keyboardDidShow };
export default AddPaymentCard;
